import { InputRule, mergeAttributes, Node } from '@tiptap/core';
import { Fragment, Node as ProseMirrorNode, NodeType, Slice } from '@tiptap/pm/model';
import { EditorState, Plugin, PluginKey, TextSelection, Transaction } from '@tiptap/pm/state';
import { Mapping } from '@tiptap/pm/transform';
import { v4 as uuidV4 } from 'uuid';

import {
  buildTkLiteral,
  findTkLiteral,
  parseTkLiteral,
  TK_DEFAULT_KIND,
  TK_EMPTY_LITERAL,
  TK_ID_ATTR,
  TK_KIND_ATTR,
  TK_LITERAL_AT_END_REGEX,
  TK_MARKER_ATTR,
  TK_MARKER_SELECTOR,
} from '../../../../utils/tkMarker';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    tkMarker: {
      /** Вставляет заготовку метки либо оборачивает выделенный текст. */
      insertTkMarker: () => ReturnType;
    };
  }
}

const NODE_NAME = 'tkMarker';

/** Позиция, которую можно разрешить в этом документе. */
function isResolvablePosition(state: EditorState, pos: number): boolean {
  return Number.isInteger(pos) && pos >= 0 && pos <= state.doc.content.size;
}

/**
 * Границы метки, внутри которой стоит позиция, либо null.
 *
 * Позиция проверяется, а не принимается на веру: `resolve` на позиции вне
 * документа БРОСАЕТ, а бросок отсюда — это сломанный редактор, а не
 * несработавшая проверка. Звать эту функцию могут с позицией, посчитанной
 * чужой арифметикой (см. правило ввода).
 */
function markerRangeAt(state: EditorState, pos: number): { from: number; to: number } | null {
  if (!isResolvablePosition(state, pos)) {
    return null;
  }

  const $pos = state.doc.resolve(pos);

  for (let { depth } = $pos; depth > 0; depth -= 1) {
    if ($pos.node(depth).type.name === NODE_NAME) {
      return { from: $pos.before(depth), to: $pos.after(depth) };
    }
  }

  return null;
}

function isInsideMarker(state: EditorState, pos: number): boolean {
  return markerRangeAt(state, pos) !== null;
}

/** Склеенное отображение позиций всей порции транзакций. */
function combinedMapping(transactions: readonly Transaction[]): Mapping {
  const mapping = new Mapping();

  transactions.forEach((transaction) => mapping.appendMapping(transaction.mapping));

  return mapping;
}

/**
 * Где эта метка стояла ДО правки, либо null.
 *
 * Сначала по `data-tk-id`: он переживает любые перемещения, тогда как позиция
 * съезжает от каждой правки слева. Если id нет или он неоднозначен — падаем на
 * обратное отображение позиции.
 */
function oldPositionOf(
  oldState: EditorState,
  markerType: NodeType,
  back: Mapping,
  pos: number,
  current: ProseMirrorNode,
): number | null {
  const { id } = current.attrs;

  if (id) {
    const matches: number[] = [];

    oldState.doc.descendants((node, nodePos) => {
      if (node.type !== markerType) {
        return undefined;
      }

      if (node.attrs.id === id) {
        matches.push(nodePos);
      }

      return false;
    });

    // Ровно одно совпадение — это она. Несколько означают, что id ничего не
    // доказывает: уникальность — лучшее усилие, а не инвариант.
    if (matches.length === 1) {
      return matches[0];
    }

    if (matches.length > 1) {
      return null;
    }
  }

  const oldPos = back.map(pos, -1);

  if (oldPos < 0 || oldPos > oldState.doc.content.size) {
    return null;
  }

  const node = oldState.doc.nodeAt(oldPos);

  return node && node.type === markerType ? oldPos : null;
}

/**
 * Прежний литерал метки и его место в испорченном содержимом, либо null.
 *
 * Место определяется ОТОБРАЖЕНИЕМ ПОЗИЦИИ, а не поиском подстроки. Поиск ищет
 * первое вхождение, а первое вхождение бывает чужим: допиши слева ровно такой
 * же «[TK: имя]», и подстрока неотличима — data-tk-id молча уехал бы к тому,
 * что автор только что набрал, а исходная метка стала бы текстом. Отображение
 * же говорит, куда переехало именно СТАРОЕ содержимое.
 *
 * Null означает «происхождение недоказуемо»: метка родилась в этой же правке,
 * позиция не отобразилась, прежнее содержимое само не было литералом, или
 * отображённое место не сходится с текстом. Тогда ремонта нет — метка
 * распускается. Распущенный текст автор увидит; уехавший id он не увидит.
 */
function originOf(
  oldState: EditorState,
  markerType: NodeType,
  forward: Mapping,
  back: Mapping,
  pos: number,
  current: ProseMirrorNode,
  text: string,
): { literal: string; at: number } | null {
  const oldPos = oldPositionOf(oldState, markerType, back, pos, current);

  if (oldPos === null) {
    return null;
  }

  const literal = (oldState.doc.nodeAt(oldPos) as ProseMirrorNode).textContent;

  if (!parseTkLiteral(literal)) {
    return null;
  }

  // Начало СОДЕРЖИМОГО старого узла — oldPos + 1. Ассоциация 1 оставляет текст,
  // вставленный ровно в эту точку, слева от отображённой позиции: дописанное в
  // начало метки и должно оказаться снаружи, перед ней.
  const at = forward.map(oldPos + 1, 1) - (pos + 1);

  if (at < 0 || at + literal.length > text.length) {
    return null;
  }

  // Последняя сверка: на отображённом месте действительно лежит прежний литерал.
  return text.slice(at, at + literal.length) === literal ? { literal, at } : null;
}

/**
 * Куда встанет одна точка селекции после того, как приписанное вынесли наружу.
 *
 * Считается ПО СМЕЩЕНИЮ в прежнем содержимом узла, а не «в конец вынесенного»:
 * последнее верно лишь для дописывания справа и увело бы курсор через всю метку
 * при дописывании слева — автор продолжал бы фразу не там, куда смотрит.
 *
 * На границе литерала точка притягивается НАРУЖУ — но только если с этой
 * стороны есть что выносить: там автор и печатал, ради чего ремонт затеян. Если
 * хвоста с этой стороны нет, точка остаётся внутри метки, где и стояла: иначе
 * правка справа переставляла бы курсор, стоящий слева, и наоборот.
 */
function positionAfterRepair(
  offset: number,
  pos: number,
  text: string,
  at: number,
  literalLength: number,
): number {
  // Слева от литерала — в вынесенный левый текст. При offset === at === 0
  // выносить некуда: левого хвоста нет, и точка остаётся внутри метки, где и
  // была. Иначе ремонт, вызванный правкой СПРАВА, переставлял бы её слева.
  if (offset < at || (offset === at && at > 0)) {
    return pos + offset;
  }

  // Справа от литерала — в вынесенный правый текст, за узлом. Симметрично:
  // если правого хвоста нет, наружу не уводим.
  const literalEnd = at + literalLength;

  if (offset > literalEnd || (offset === literalEnd && literalEnd < text.length)) {
    return pos + at + literalLength + 2 + (offset - literalEnd);
  }

  // Внутри литерала — остаёмся внутри метки.
  return pos + offset + 1;
}

/**
 * Селекция после ремонта узла, либо null.
 *
 * Null — когда вмешиваться не следует: селекция нетекстовая или хотя бы одна
 * её точка лежит вне этого узла. Там штатное отображение позиций справится
 * лучше, чем наши догадки.
 */
function selectionAfterRepair(
  selection: EditorState['selection'],
  pos: number,
  text: string,
  at: number,
  literalLength: number,
): { anchor: number; head: number } | null {
  if (!(selection instanceof TextSelection)) {
    return null;
  }

  const start = pos + 1;
  const anchorOffset = selection.anchor - start;
  const headOffset = selection.head - start;
  const inside = (offset: number) => offset >= 0 && offset <= text.length;

  if (!inside(anchorOffset) || !inside(headOffset)) {
    return null;
  }

  // Обе точки переносятся ПО ОТДЕЛЬНОСТИ, поэтому направление выделения
  // сохраняется. Единая замена узла отобразила бы обе в конец замены, то есть
  // молча схлопнула бы выделение — для автора это потеря выделенного куска.
  return {
    anchor: positionAfterRepair(anchorOffset, pos, text, at, literalLength),
    head: positionAfterRepair(headOffset, pos, text, at, literalLength),
  };
}

/** Текст под маркой `code` — не проза, литералы в нём не распознаём. */
function isCodeText(node: ProseMirrorNode): boolean {
  return node.marks.some((mark) => mark.type.name === 'code');
}

/**
 * Режет текстовый узел на «до / метка / после», оборачивая найденные литералы.
 *
 * Марки окружающего текста сохраняются, а внутрь метки не попадают: схема их
 * там не допускает (`marks: ''`), и литерал должен остаться литералом.
 */
function splitTextIntoMarkers(node: ProseMirrorNode, markerType: NodeType): ProseMirrorNode[] {
  const { schema } = markerType;
  const parts: ProseMirrorNode[] = [];
  let rest = node.text ?? '';

  for (let found = findTkLiteral(rest); found; found = findTkLiteral(rest)) {
    if (found.from > 0) {
      parts.push(schema.text(rest.slice(0, found.from), node.marks));
    }

    parts.push(
      markerType.create(
        { kind: found.kind, id: uuidV4() },
        schema.text(rest.slice(found.from, found.to)),
      ),
    );

    rest = rest.slice(found.to);
  }

  if (parts.length === 0) {
    return [node];
  }

  if (rest) {
    parts.push(schema.text(rest, node.marks));
  }

  return parts;
}

/**
 * Единственный обход вставляемого фрагмента, с явно заданным порядком.
 *
 * Сначала узел опознаётся как метка — тогда ему перевыдаётся id и внутрь мы не
 * спускаемся. Иначе текстовые узлы режутся на метки и текст вокруг. Двух
 * проходов быть не должно: именно на их порядке возникает двойная перевыдача id.
 *
 * Ранний выход из метки — экономия, а не защита от вложенности: вложенность
 * невозможна и без него, потому что собственное содержимое метки объявлено как
 * `text*` и метку внутрь себя не допускает. Проверено перестановкой — спуск
 * внутрь ничего не ломает.
 *
 * Перевыдача id нужна потому, что копия метки — это новая метка: иначе в
 * документе оказались бы две метки с одним data-tk-id.
 *
 * Распознавание нужно потому, что вставленный со стороны текст приходит с
 * голыми литералами — правило ввода их не видело.
 *
 * `parentType` — контекст, в который попадёт содержимое. Для верхнего уровня
 * среза родителя нет вовсе (простой текст), поэтому туда передаётся тип узла
 * назначения. Допустимость проверяется по схеме, а не перечислением мест:
 * в `codeBlock` с его `content: 'text*'` инлайновый узел дал бы невалидный срез.
 */
function transformPastedFragment(
  fragment: Fragment,
  markerType: NodeType,
  parentType: NodeType | null,
): Fragment {
  const nodes: ProseMirrorNode[] = [];

  const allowed = parentType ? parentType.contentMatch.matchType(markerType) !== null : false;

  fragment.forEach((node) => {
    if (node.type === markerType) {
      // Место назначения метку не принимает — разворачиваем её в текст.
      // Оставить узел как есть нельзя: срез окажется несовместим с местом
      // вставки, и подгонка ProseMirror может выбросить литерал целиком — то
      // есть потерять ровно то, ради сохранности чего скобки лежат в тексте.
      nodes.push(
        ...(allowed
          ? [markerType.create({ ...node.attrs, id: uuidV4() }, node.content, node.marks)]
          : node.content.content),
      );

      return;
    }

    if (node.isText) {
      nodes.push(
        ...(allowed && !isCodeText(node) ? splitTextIntoMarkers(node, markerType) : [node]),
      );

      return;
    }

    nodes.push(node.copy(transformPastedFragment(node.content, markerType, node.type)));
  });

  return Fragment.fromArray(nodes);
}

/**
 * Редакторская метка To Come: «[TK: найти имя]».
 *
 * Узел, а не марка, по двум причинам. Первая: исключить содержимое из
 * статистики можно только по имени узла (`textSerializers`) — про марки
 * `getText()` не знает ничего. Вторая: марки ProseMirror молча склеивает и
 * молча делит, а метке нужны границы и собственная личность.
 *
 * Содержимое узла — литерал целиком, вместе со скобками, настоящим текстом.
 * Скобки НЕ рисуются через CSS: иначе везде, куда наши стили не дотянулись
 * (docx, pdf, копипаста, чужой рендер), метка бесшумно превратилась бы в прозу
 * — «И вдруг из темноты вышел придумать имя страннику».
 *
 * `renderText` намеренно НЕ определён: он пишется в `schema.toText`, который
 * подхватывает не только статистика, но и сериализатор клипборда — копирование
 * абзаца с меткой в почту теряло бы «[TK: …]». Статистика исключает метки явно.
 */
export const TkMarker = Node.create({
  name: NODE_NAME,

  /**
   * Выше стандартных 100, чтобы перехват Enter и Shift-Enter срабатывал раньше
   * чужих обработчиков — например `CustomEnterBehavior` в text_client. Иначе
   * победитель зависел бы от порядка расширений в массиве, то есть от случая.
   */
  priority: 1000,

  inline: true,
  group: 'inline',

  /**
   * `text*`, хотя план просил `text+`.
   *
   * ProseMirror запрещает `text+` на уровне построения схемы: «Only
   * non-generatable nodes (text) in a required position» — обязательную позицию
   * он обязан уметь сгенерировать сам, а текст так не умеет. Узел с `text+`
   * попросту не собирается.
   *
   * Потери при этом нет. `text+` затыкал ровно одну дыру — пустой узел нулевой
   * ширины, остающийся после удаления содержимого. Её полностью закрывает
   * инвариант целостности: пустая строка не проходит `parseTkLiteral`, поэтому
   * такой узел отвергается при загрузке и разворачивается при правке. Инвариант
   * строже схемы — он ловит и пустое содержимое, и испорченный литерал.
   */
  content: 'text*',

  /** Внутри метки форматирования нет — литерал должен остаться литералом. */
  marks: '',

  selectable: true,

  addAttributes() {
    return {
      // Оба атрибута маппятся явно: по умолчанию TipTap пишет имя поля как есть
      // (kind="TK"), а нам нужно data-kind — как и в соседнем Highlight.
      kind: {
        default: TK_DEFAULT_KIND,
        /**
         * Вид выводится из ЛИТЕРАЛА, а не берётся из атрибута на веру.
         *
         * `data-kind` правит кто угодно — в том числе LLM через MCP. Поверив
         * атрибуту, мы завели бы в документе «метку» с видом, которого не
         * существует, и будущая фильтрация по виду молча её потеряла бы.
         * Так атрибут становится чистой производной содержимого, и разойтись
         * с ним по построению не может.
         */
        parseHTML: (element) => parseTkLiteral(element.textContent ?? '')?.kind ?? TK_DEFAULT_KIND,
        renderHTML: (attributes) => ({ [TK_KIND_ATTR]: attributes.kind }),
      },
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute(TK_ID_ATTR),
        renderHTML: (attributes) => (attributes.id ? { [TK_ID_ATTR]: attributes.id } : {}),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: TK_MARKER_SELECTOR,
        /**
         * Первая из трёх точек, где держится инвариант целостности.
         *
         * Span с испорченным литералом не становится узлом вовсе — содержимое
         * остаётся обычным текстом. Без этого заметка, переписанная через MCP
         * или пришедшая из старой версии, принесла бы «метку», внутри которой
         * лежит произвольный текст: `appendTransaction` её не увидит, потому что
         * при первичном построении состояния редактора транзакции ещё нет.
         */
        getAttrs: (element) =>
          parseTkLiteral((element as HTMLElement).textContent ?? '') ? null : false,
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, { [TK_MARKER_ATTR]: '' }), 0];
  },

  addCommands() {
    return {
      insertTkMarker:
        () =>
        ({ state, tr, dispatch }) => {
          const { selection } = state;
          const { $from, $to, empty } = selection;

          // Внутри метки вставлять нечего — вложенности не бывает.
          if (isInsideMarker(state, $from.pos) || isInsideMarker(state, $to.pos)) {
            return false;
          }

          if (empty) {
            const start = $from.pos;

            if (dispatch) {
              tr.replaceSelectionWith(
                this.type.create(
                  { kind: TK_DEFAULT_KIND, id: uuidV4() },
                  state.schema.text(TK_EMPTY_LITERAL),
                ),
                false,
              );
              // Курсор внутрь, перед закрывающую скобку — чтобы сразу печатать.
              tr.setSelection(TextSelection.create(tr.doc, start + TK_EMPTY_LITERAL.length));
            }

            return true;
          }

          // Оборачиваем только одноблочное текстовое выделение. Затолкать
          // несколько абзацев или чужой inline-узел внутрь метки всё равно
          // нельзя — честный отказ лучше испорченного документа.
          if (!(selection instanceof TextSelection) || !$from.sameParent($to)) {
            return false;
          }

          let plainText = true;

          state.doc.nodesBetween($from.pos, $to.pos, (node, pos) => {
            if (pos >= $from.pos && !node.isText && node.isInline) {
              plainText = false;
            }

            return undefined;
          });

          if (!plainText) {
            return false;
          }

          const payload = state.doc.textBetween($from.pos, $to.pos);
          const literal = buildTkLiteral(TK_DEFAULT_KIND, payload);

          // Выделение со скобкой внутри дало бы литерал, который не проходит
          // собственную валидацию, — метка рассыпалась бы сразу после создания.
          if (!parseTkLiteral(literal)) {
            return false;
          }

          if (dispatch) {
            tr.replaceRangeWith(
              $from.pos,
              $to.pos,
              this.type.create({ kind: TK_DEFAULT_KIND, id: uuidV4() }, state.schema.text(literal)),
            );
          }

          return true;
        },
    };
  },

  addKeyboardShortcuts() {
    /**
     * Enter внутри метки разорвал бы её надвое: получились бы два узла с
     * одинаковым data-tk-id и битым литералом — «[TK: при» и «думать имя]».
     * Вместо разрыва выводим курсор за метку; следующий Enter сработает обычно.
     *
     * Shift-Enter перехватываем отдельно: в text_client он отдельной командой
     * зовёт splitBlock, и про него легко забыть.
     */
    const escapeMarker = () => {
      const { state } = this.editor;

      // Перехват существует ради КУРСОРА внутри метки. Живое выделение — другой
      // случай: правка обязана произойти, иначе мы молча съедаем выделение и
      // увозим курсор. Испорченный литерал подберёт инвариант целостности.
      if (!state.selection.empty) {
        return false;
      }

      const range = markerRangeAt(state, state.selection.from);

      if (!range) {
        return false;
      }

      return this.editor.commands.setTextSelection(range.to);
    };

    return {
      Enter: escapeMarker,
      'Shift-Enter': escapeMarker,
    };
  },

  addProseMirrorPlugins() {
    const markerType = this.type;

    return [
      new Plugin({
        key: new PluginKey('tkMarkerPasteIds'),

        props: {
          transformPasted: (slice, view) =>
            new Slice(
              transformPastedFragment(
                slice.content,
                markerType,
                view.state.selection.$from.parent.type,
              ),
              slice.openStart,
              slice.openEnd,
            ),
        },
      }),

      new Plugin({
        key: new PluginKey('tkMarkerIntegrity'),

        /**
         * Вторая из трёх точек инварианта: правка.
         *
         * Схема гарантирует форму узла, но не содержимое — автор может стереть
         * скобку, и метка осталась бы меткой с испорченным литералом: панель её
         * не разберёт, а статистика продолжит молча исключать текст внутри.
         *
         * Непорядок бывает двух разных сортов, и отвечать на них надо по-разному.
         *
         * ПЕРВЫЙ — литерал цел, но к нему что-то приписано. Так выходит всегда,
         * когда автор напечатал метку и продолжил фразу: граница inline-элемента
         * в contenteditable «липкая», и символ садится ВНУТРЬ узла. Это свойство
         * браузеров, а не нашего кода: проверено живой проверкой, курсор ставился
         * явно за закрывающий тег — символ всё равно уходил внутрь. Перехватить
         * все пути ввода нельзя (обычный набор, IME, автозамена, вставка,
         * перетаскивание, программная правка — у каждого своя дорога), но все они
         * заканчиваются транзакцией, а мы смотрим каждую. Поэтому приписанное
         * выносится наружу, а метка остаётся меткой.
         *
         * ВТОРОЙ — литерал сломан по существу. Тогда метка по-прежнему
         * распускается в текст: это единственный способ автора её снять, и
         * отнимать его нельзя.
         *
         * Какой литерал считать своим — решается по ПРОИСХОЖДЕНИЮ, а не поиском.
         * Наивное «взять первое совпадение» отдало бы тождество метки тому, что
         * автор случайно набрал слева: в «[TK: новая][TK: исходная]» первым идёт
         * чужой литерал, и прежний data-tk-id уехал бы к нему.
         *
         * Обход по всему документу, без вычисления изменённых диапазонов.
         * Документ — одна заметка, `descendants` по ней стоит доли миллисекунды,
         * зато вместе с арифметикой границ через несколько StepMap исчезает
         * целый класс инвариантов, срабатывающих через раз.
         */
        appendTransaction: (transactions, oldState, newState) => {
          if (!transactions.some((transaction) => transaction.docChanged)) {
            return null;
          }

          const damaged: { pos: number; node: ProseMirrorNode }[] = [];

          newState.doc.descendants((node, pos) => {
            if (node.type !== markerType) {
              return undefined;
            }

            if (!parseTkLiteral(node.textContent)) {
              damaged.push({ pos, node });
            }

            // Внутрь метки спускаться незачем: там только текст.
            return false;
          });

          if (!damaged.length) {
            return null;
          }

          const transaction = newState.tr;
          const forward = combinedMapping(transactions);
          const back = forward.invert();

          // Массив, а не одиночная переменная: анализ типов не заглядывает в
          // колбэк и после forEach считал бы её так и оставшейся null.
          const moved: { anchor: number; head: number; afterStep: number }[] = [];

          // С конца к началу: правки правее уже сделаны и позиций левее не трогают.
          damaged.reverse().forEach(({ pos, node }) => {
            const text = node.textContent;
            const origin = originOf(oldState, markerType, forward, back, pos, node, text);

            if (!origin) {
              if (text) {
                transaction.replaceWith(
                  pos,
                  pos + node.nodeSize,
                  newState.schema.text(text, node.marks),
                );
              } else {
                transaction.delete(pos, pos + node.nodeSize);
              }

              return;
            }

            const { literal, at } = origin;
            const left = text.slice(0, at);
            const right = text.slice(at + literal.length);
            const parts: ProseMirrorNode[] = [];

            if (left) {
              parts.push(newState.schema.text(left, node.marks));
            }

            // attrs и marks переносятся: это ТА ЖЕ метка, а не новая. Иначе
            // сменился бы data-tk-id (переход из панели повёл бы не туда) и
            // слетело бы форматирование самого узла.
            parts.push(markerType.create(node.attrs, newState.schema.text(literal), node.marks));

            if (right) {
              parts.push(newState.schema.text(right, node.marks));
            }

            transaction.replaceWith(pos, pos + node.nodeSize, parts);

            const next = selectionAfterRepair(newState.selection, pos, text, at, literal.length);

            if (next !== null) {
              moved.push({ ...next, afterStep: transaction.steps.length });
            }
          });

          const [placed] = moved;

          if (placed) {
            // Правки ЛЕВЕЕ выделения делаются позже (идём справа налево),
            // поэтому позиции домапливаем шагами, добавленными после их расчёта.
            const rest = transaction.mapping.slice(placed.afterStep);

            transaction.setSelection(
              TextSelection.create(transaction.doc, rest.map(placed.anchor), rest.map(placed.head)),
            );
          }

          return transaction;
        },
      }),
    ];
  },

  addInputRules() {
    return [
      new InputRule({
        /**
         * Регекс заякорен справа: TipTap отсчитывает начало заменяемого
         * диапазона от конца совпадения, и незаякоренный вариант утащил бы
         * в метку текст, набранный слева от неё.
         */
        find: TK_LITERAL_AT_END_REGEX,

        handler: ({ state, range, match }) => {
          const literal = match[0];
          const parsed = parseTkLiteral(literal);

          if (!parsed) {
            return null;
          }

          /**
           * Диапазон отвергается, а НЕ зажимается в границы документа.
           *
           * TipTap отсчитывает начало от конца совпадения:
           * `range.from = from - (match[0].length - text.length)`. Пока текст
           * приходит по одному символу, разность неотрицательна. Но экранная
           * клавиатура с IME, автозамена и программная вставка присылают кусок
           * целиком, и кусок ДЛИННЕЕ совпадения уводит `range.from` ПРАВЕЕ
           * `range.to`.
           *
           * Опасны оба исхода, но по-разному. В короткой заметке позиция уходит
           * за конец документа, и `resolve` бросает — это живая проверка поймала
           * как падение редактора. В длинной позиция остаётся ВНУТРИ документа,
           * проверка границ её пропускает, и без условия `from > to` замена шла
           * задом наперёд: набранное молча пропадало, соседний текст дублировался.
           * Второе хуже — оно не падает, а портит текст. Держит его именно третье
           * условие, а не первые два.
           *
           * Зажать позицию в границы было бы тоже молчаливо плохо: получилась бы
           * выдуманная позиция, и правило заменило бы не тот текст. Отказ
           * означает, что кусок вставится как обычный текст, — а метку из него
           * подберёт распознавание при вставке.
           */
          if (
            !isResolvablePosition(state, range.from) ||
            !isResolvablePosition(state, range.to) ||
            range.from > range.to
          ) {
            return null;
          }

          // Внутри метки скобка остаётся просто скобкой — вложенность запрещена.
          if (isInsideMarker(state, range.from) || isInsideMarker(state, range.to)) {
            return null;
          }

          // В документ ложится ровно то, что напечатал автор: кириллическое
          // «[ТК: …]» остаётся кириллическим. Канонизируется только kind.
          state.tr.replaceRangeWith(
            range.from,
            range.to,
            this.type.create({ kind: parsed.kind, id: uuidV4() }, state.schema.text(literal)),
          );

          return undefined;
        },
      }),
    ];
  },
});
